import { observable, computed, makeObservable } from 'mobx'
import uuidv4 from 'uuid/v4'
import * as R from 'ramda'
import { unescapeHtmlTags, mapToJSON } from '../utils/helpers'
import HeaderGroup from './HeaderGroup'
import { getTitle } from '../utils/QuestionHelper'
import i18n from 'i18next'

class QuestionMetadata {
    /**
     * @param {Boolean} isQlistRecommended = false
     * @param {Boolean} mandatory
     * @param {Int} oid
     * @param {Long} clientId
     * @param {Long} categoryId
     * @param {Boolean} isGlobal
     * @param {Boolean} isBenchmarked
     * @param {Boolean} isEditable
     * @param {String=} kpiType
     */
    constructor({
        isQlistRecommended = false,
        isMandatory,
        isActive = true,
        oid,
        clientId,
        initialLocale,
        categoryId,
        isGlobal,
        isNewGlobal,
        isBenchmarked,
        isFilterBenchmarked,
        isEditable,
        isRanked,
        kpiType,
    }) {
        this.isQlistRecommended = isQlistRecommended
        this.isMandatory = isMandatory
        this.isActive = isActive
        this.initialLocale = initialLocale
        this.oid = oid
        this.clientId = clientId
        this.categoryId = categoryId
        this.isGlobal = isGlobal
        this.isNewGlobal = isNewGlobal
        this.isBenchmarked = isBenchmarked
        this.isFilterBenchmarked = isFilterBenchmarked
        this.isEditable = isEditable
        this.isRanked = Boolean(isRanked)
        this.kpiType = kpiType
    }

    toJSON() {
        return {
            isQlistRecommended: this.isQlistRecommended,
            isMandatory: this.isMandatory,
            isActive: this.isActive,
            initialLocale: this.initialLocale,
            oid: this.oid,
            clientId: this.clientId,
            categoryId: this.categoryId,
            isGlobal: this.isGlobal,
            isNewGlobal: this.isNewGlobal,
            isBenchmarked: this.isBenchmarked,
            isFilterBenchmarked: this.isFilterBenchmarked,
            isEditable: this.isEditable,
            isRanked: Boolean(this.isRanked),
            kpiType: this.kpiType,
        }
    }
}

class QuestionLang {
    /**
     * @param {Object} options
     * @param {UUID} options.id
     * @param {String} options.text
     */
    constructor({
        id,
        text,
    }) {
        this.id = id
        this.text = text
    }

    toJSON() {
        return {
            id: this.id,
            text: this.text,
            originalId: this.originalId,
            legacyId: this.legacyId,
        }
    }
}

class LocalizedQuestionContent {
    /**
     * @param {Object} options
     * @param {Map<string, QuestionLang>} options.shortTitle
     * @param {Map<string, QuestionLang>=} options.longTitle
     * @param {Map<string, QuestionLang>=} options.instructions
     * @param {Map<string, QuestionLang>=} options.aboveText
     * @param {Map<string, QuestionLang>=} options.belowText
     * @param {Map<string, QuestionLang>=} options.content
     */

    toJSON() {
        const json = {}
        console.log('LocalizedQuestionContent toJSON')
        this.shortTitle.forEach((value, key) => {
            if (!json.shortTitle) {
                json.shortTitle = {}
            }
            json.shortTitle[key] = value.toJSON()
        })
        console.log('shortTitle')
        if (this.longTitle) {
            this.longTitle.forEach((value, key) => {
                if (!json.longTitle) {
                    json.longTitle = {}
                }
                json.longTitle[key] = value.toJSON()
            })
            console.log('longTitle')
        }
        if (this.instructions) {
            this.instructions.forEach((value, key) => {
                if (!json.instructions) {
                    json.instructions = {}
                }
                json.instructions[key] = value.toJSON()
            })
        }
        if (this.aboveText) {
            this.aboveText.forEach((value, key) => {
                if (!json.aboveText) {
                    json.aboveText = {}
                }
                json.aboveText[key] = value.toJSON()
            })
        }
        if (this.belowText) {
            this.belowText.forEach((value, key) => {
                if (!json.belowText) {
                    json.belowText = {}
                }
                json.belowText[key] = value.toJSON()
            })
        }
        if (this.content) {
            this.content.forEach((value, key) => {
                if (!json.content) {
                    json.content = {}
                }
                json.content[key] = value.toJSON()
            })
        }
        // console.log('LocalizedQuestionContent toJSON done')
        return json
    }

    getTitleByLocale(title, locale, defaultLocale, escape = true) {
        // console.log(`getTitleByLocale ${title} ${locale} ${defaultLocale} ${escape}`)
        if (!this[title]) {
            // console.log(`${title} is not part of LocalizedQuestionContent`)
            return ''
        }
        let content = ''
        if (this[title].has(locale)) {
            content = this[title].get(locale).text
        } else if (this[title].has(defaultLocale)) {
            // console.log(`Locale ${locale} doesn't exist for title ${title} in LocalizedQuestionContent -> revert to default ${defaultLocale}`)
            content = this[title].get(defaultLocale).text
        } else if (this[title].has(this.initialLocale)) {
            // console.log(`Locale ${defaultLocale} doesn't exist for title ${title} in LocalizedQuestionContent -> revert to initial ${this.initialLocale}`)
            content = this[title].get(this.initialLocale).text
            // console.log(this[title].get(this.initialLocale).text)
        } else {
            // console.log(`No content found for locale ${locale}`)
        }
        return escape ? unescapeHtmlTags(content) : content
    }

    constructor({
        shortTitle,
        longTitle,
        instructions,
        aboveText,
        belowText,
        content,
    }, initialLocale) {
        this.initialLocale = initialLocale
        this.shortTitle = shortTitle instanceof Map
            ? shortTitle : new Map(Object.entries(shortTitle || {})
                .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))

        if (longTitle) {
            this.longTitle = longTitle instanceof Map
                ? longTitle : new Map(Object.entries(longTitle || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }

        if (instructions) {
            this.instructions = instructions instanceof Map
                ? instructions : new Map(Object.entries(instructions || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }

        if (aboveText) {
            this.aboveText = aboveText instanceof Map
                ? aboveText : new Map(Object.entries(aboveText || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }

        if (belowText) {
            this.belowText = belowText instanceof Map
                ? belowText : new Map(Object.entries(belowText || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }

        if (content) {
            this.content = content instanceof Map
                ? content : new Map(Object.entries(content || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }
    }
}

class Question {
    /**
     * @param {Object} options
     * @param {QuestionMetadata} options.metadata
     * @param {LocalizedQuestionContent} options.content
     * @param {UUID=} options.id
     */

    @observable liveResponses = false

    @observable overrideEdit = false

    constructor({
        metadata,
        content,
        id,
        questionTypeCode,
    }) {
        makeObservable(this)
        this.metadata = metadata instanceof QuestionMetadata ? metadata : new QuestionMetadata(metadata)
        this.content = content instanceof LocalizedQuestionContent ? content : new LocalizedQuestionContent(content, this.metadata.initialLocale)
        this.id = id,
        this.questionTypeCode = questionTypeCode
    }

    toJSON() {
        return {
            metadata: this.metadata.toJSON(),
            content: this.content.toJSON(),
            id: this.id,
        }
    }

    hasHeaderGroup() {
        return false
    }

    setContentText = (value, target, locale) => {
        if (this.content[target]) {
            if (this.content[target].has(locale)) {
                this.content[target].get(locale).text = value
            } else if (value !== '') {
                this.content[target].set(locale, new QuestionLang({ text: value }))
            }
        } else {
            this.content[target] = new Map()
            this.content[target].set(locale, new QuestionLang({ text: value }))
        }
    }

    hasDashboardRange(range) {
        // store.question.settings.report.dashboardBarRanges.red
        let hasRange = this.settings
            && this.settings.report
            && this.settings.report.dashboardBarRanges

        if (range) {
            hasRange = hasRange
                && Object.hasOwnProperty.call(this.settings.report.dashboardBarRanges, range)
        }
        return hasRange
    }
    
    isText() {
        return ['TX', 'TA', 'TL', 'UV'].includes(this.questionTypeCode)
    }

    isMulti() {
        return ['RD', 'CH', 'TB', 'OD', 'PC', 'RT', 'SG'].indexOf(this.questionTypeCode) > -1
    }

    isDoubleAxis() {
        return this instanceof DoubleAxisQuestion
    }

    isSingleAxis() {
        return this instanceof SingleAxisQuestion
    }

    isValid() {
        let valid = true
        const hasShortTitle = Array.from(this.content.shortTitle)
            .reduce((hasTitle, [_, questionLang]) => (
                hasTitle || questionLang.text !== ''
            ), false)
        valid = valid && hasShortTitle
        return valid
    }

    static fromJSON(json) {
        let question
        const { questionTypeCode } = json
        switch (questionTypeCode) {
            case 'CH':
                question = new MultipleChoiceQuestion(json)
                break
            case 'CO':
                question = new CountryListQuestion(json)
                break
            case 'I':
                question = new NumericQuestion(json)
                break
            case 'OD':
                question = new PriorityQuestion(json)
                break
            case 'PC':
                question = new PercentageQuestion(json)
                break
            case 'RD':
                question = new SingleChoiceQuestion(json)
                break
            case 'UV':
                question = new UrlVariableQuestion(json)
                break
            case 'TL':
                question = new TextBoxListQuestion(json)
                break
            case 'FM':
                question = new FormQuestion(json)
                break
            case 'NF':
                question = new NumberFormQuestion(json)
                break
            case 'DT':
                question = new DateQuestion(json)
                break
            case 'RT':
                question = new RatingQuestion(json)
                break
            case 'SB':
                question = new DropdownListQuestion(json)
                break
            case 'ST':
                question = new StaticText(json)
                break
            case 'TA':
                question = new TextAreaQuestion(json)
                break
            case 'TB':
                question = new GridQuestion(json)
                break
            case 'SG':
                question = new SmartGridQuestion(json)
                break
            case 'TX':
                question = new TextBoxQuestion(json)
                break
            default:
                question = null
        }

        return question
    }
}

class QuestionOptionLang {
    @observable text

    constructor({
        id,
        text,
    }) {
        makeObservable(this)
        this.id = id
        this.text = text
            .replace(/&amp;/g, '&')
            .replace(/&nbsp;/g, ' ')
    }

    toJSON() {
        return {
            id: this.id,
            text: this.text,
        }
    }
}

class QuestionOption {
    @observable allowEdit = false

    @observable isHidden = false

    @observable isLocked = false

    @observable optionOtherIsMandatory = false

    @observable hasOptionOther = false

    @observable metricIds = []

    @observable defaultMetric = null

    isNew = false

    fallbackTranslationMsg = ''

    constructor({
        id,
        reportingValue,
        langs,
        priority,
        isHidden = false,
        originalQuestionOptionId = 0,
        optionOtherIsMandatory = false,
        hasOptionOther = false,
        isExclusive = false,
        fieldType = 'RD',
        isLocked = false,
        isNonReporting = false,
        headerGroupId,
        otherPiping,
        metricIds = [],
        defaultMetric = null,
        justAdded = false,
    }) {
        makeObservable(this)
        this.isNew = R.isNil(id)
        this.renderKey = this.id || uuidv4()
        this.id = id || uuidv4()
        this.reportingValue = reportingValue
        this.langs = langs instanceof Map
            ? langs : new Map(Object.entries(langs)
                .map(([langCode, questionOptionLang]) => (
                    [langCode, new QuestionOptionLang(questionOptionLang)]
                )))
        this.priority = priority
        this.isHidden = isHidden
        this.originalQuestionOptionId = originalQuestionOptionId
        this.optionOtherIsMandatory = optionOtherIsMandatory
        this.hasOptionOther = hasOptionOther
        this.isExclusive = isExclusive
        this.fieldType = fieldType
        this.isLocked = isLocked
        this.isNonReporting = isNonReporting
        this.headerGroupId = headerGroupId
        this.otherPiping = otherPiping
        this.metricIds = metricIds,
        this.defaultMetric = defaultMetric
        this.justAdded = justAdded
    }

    clone() {
        return new QuestionOption({
            id: this.id,
            reportingValue: this.reportingValue,
            langs: this.langs,
            priority: this.priority,
            isHidden: this.isHidden,
            originalQuestionOptionId: this.originalQuestionOptionId,
            optionOtherIsMandatory: this.optionOtherIsMandatory,
            hasOptionOther: this.hasOptionOther,
            isExclusive: this.isExclusive,
            fieldType: this.fieldType,
            isLocked: this.isLocked,
            isNonReporting: this.isNonReporting,
            headerGroupId: this.headerGroupId,
            metricIds: this.metricIds,
            defaultMetric: this.defaultMetric,
        })
    }

    addLangText(locale, questionOptionLang) {
        this.langs.set(locale, new QuestionOptionLang(questionOptionLang))
    }

    getTitle(locale, defaultLocale, initialLocale, availableLocales = []) {
        let text = ''
        if (this.langs.get(locale)) {
            ({ text } = this.langs.get(locale))
            this.fallbackTranslationMsg = undefined
        } else if (this.langs.get(defaultLocale)) {
            ({ text } = this.langs.get(defaultLocale))
            this.fallbackTranslationMsg = i18n.t('q_fallback_translation_survey_primary', {
                targetLocale: availableLocales.find((l) => l.value === locale)?.label || locale,
                usedLocale: availableLocales.find((l) => l.value === defaultLocale)?.label || defaultLocale,
            })
        } else if (this.langs.get(initialLocale)) {
            ({ text } = this.langs.get(initialLocale))
            this.fallbackTranslationMsg = i18n.t('q_fallback_translation_question_initial', {
                targetLocale: availableLocales.find((l) => l.value === locale)?.label || locale,
                usedLocale: availableLocales.find((l) => l.value === initialLocale)?.label || initialLocale,
            })
        }
        if (text === '') {
            this.fallbackTranslationMsg = i18n.t('q_fallback_translation_blank', {
                targetLocale: availableLocales.find((l) => l.value === locale)?.label || locale,
            })
        }
        return unescapeHtmlTags(text)
    }

    toJSON() {
        // verify that all entries are a QuestionOptionLang class before attempting to mapToJSON
        this.langs = new Map(Object.entries(Object.fromEntries(this.langs))
        .map(([langCode, questionOptionLang]) => (
            [langCode, new QuestionOptionLang(questionOptionLang)]
        )))
        const json = {
            langs: mapToJSON(this.langs),
            isHidden: this.isHidden,
            originalQuestionOptionId: this.originalQuestionOptionId,
            optionOtherIsMandatory: this.optionOtherIsMandatory,
            hasOptionOther: this.hasOptionOther,
            isExclusive: this.isExclusive,
            fieldType: this.fieldType,
            isLocked: this.isLocked,
            isNonReporting: this.isNonReporting,
            headerGroupId: this.headerGroupId,
            otherPiping: this.hasOptionOther ? this.otherPiping : undefined,
            metricIds: this.metricIds.slice(),
            defaultMetric: this.defaultMetric,
        }

        if (this.id) {
            json.id = this.id
        }

        if (this.reportingValue || this.reportingValue === 0) {
            json.reportingValue = this.reportingValue
        }

        if (this.priority) {
            json.priority = this.priority
        }

        return json
    }
}

const questionOptionSortType = {
    AlphaAsc: 'AlphaAsc',
    AlphaDesc: 'AlphaDesc',
    NumericAsc: 'NumericAsc',
    NumericDesc: 'NumericDesc',
    Randomized: 'Randomized',
}

class OptionSet {
    @observable options

    @observable sortType

    @observable hasMetrics

    @observable hasNumericCodes
    /**
     * @param {Object} options
     * @param {UUID=} options.id
     * @param {String} options.axis
     * @param {Array<QuestionOption>} options.options
     * @param {Boolean} options.hasNumericCodes
     * @param {Boolean} options.isExclusive
     * @param {Boolean} options.hasMetrics
     * @param {questionOptionSortType=} options.sortType
     */

    constructor({
        id,
        axis = 'y',
        options = [],
        hasNumericCodes = false,
        hasMetrics = false,
        // randomizedOptions = false,
        isExclusive = false,
        // alphasort = false,
        // numSort = false,
        sortType,
    }) {
        makeObservable(this)
        this.id = id
        this.axis = axis
        this.options = options
            .map((option) => (option instanceof QuestionOption ? option : new QuestionOption(option)))
        this.hasNumericCodes = hasNumericCodes
        this.hasMetrics = hasMetrics
        // this.randomizedOptions = randomizedOptions
        this.isExclusive = isExclusive
        this.sortType = sortType
        this.hasMetrics = hasMetrics
        // this.alphasort = alphasort
        // this.numSort = numSort
    }

    @computed get isAlphaSort() {
        return this.sortType === questionOptionSortType.AlphaAsc
            || this.sortType === questionOptionSortType.AlphaDesc
    }

    @computed get isNumSort() {
        return this.sortType === questionOptionSortType.NumericAsc
            || this.sortType === questionOptionSortType.NumericDesc
    }

    @computed get isRandomized() {
        return this.sortType === questionOptionSortType.Randomized
    }

    addOption(option, justAdded = false) {
        console.log(`addOption ${option}, ${justAdded}`)
        const priority = this.options.length + 1
        const questionOption = new QuestionOption(Object.assign({}, option, { priority, justAdded }))
        if (option.allowEdit) {
            questionOption.allowEdit = true
        }
        this.options.push(questionOption)
    }

    addOptionBulk(options) {
        this.options = options.map((option, index) => {
            const priority = index + 1
            return new QuestionOption(Object.assign({}, option, { priority }))
        })
    }

    changeSorting(sorting, checked) {
        this.numSort = false
        this.alphasort = false
        this.randomizedOptions = false
        switch (sorting) {
            case 'numSort':
                this.sortType = checked ? questionOptionSortType.NumericAsc : undefined
                break
            case 'alphasort':
                this.sortType = checked ? questionOptionSortType.AlphaAsc : undefined
                break
            case 'randomizedOptions':
                this.sortType = checked ? questionOptionSortType.Randomized : undefined
                break
            default:
                this.sortType = undefined
        }
    }

    toggleNumericCodes() {
        this.hasNumericCodes = !this.hasNumericCodes
        if (!this.hasNumericCodes) {
            if (this.sortType === 'NumericAsc') {
                this.sortType = undefined
            }
            this.numSort = false
            this.options.forEach((option) => {
                Reflect.deleteProperty(option, 'reportingValue')
            })
        }
    }

    toJSON() {
        return {
            id: this.id,
            axis: this.axis,
            options: this.options.map((o) => o.toJSON()),
            hasNumericCodes: this.hasNumericCodes,
            hasMetrics: this.hasMetrics,
            isExclusive: this.isExclusive,
            hasMetrics: this.hasMetrics,
            sortType: this.sortType,
        }
    }
}

class FormOptionSet {
    constructor({
        id,
        renderKey,
        allowEdit = false,
        isHidden = false,
        mandatory = false,
        question,
    }) {
        this.id = id
        this.renderKey = renderKey || this.id || uuidv4()
        this.allowEdit = allowEdit
        this.isHidden = isHidden
        this.mandatory = mandatory

        if (question) {
            switch (question.questionTypeCode) {
                case 'I':
                    this.question = new NumericQuestion(question)
                    break
                case 'TX':
                    this.question = new TextBoxQuestion(question)
                    break
                case 'SB':
                    this.question = new DropdownListQuestion(question)
                    break
                case 'DT':
                    this.question = new DateQuestion(question)
                    break
                default:
                    break
            }
        }
    }

    clone() {
        return new FormOptionSet({
            id: this.id,
            renderKey: this.renderKey,
            allowEdit: this.allowEdit,
            isHidden: this.isHidden,
            mandatory: this.mandatory,
            question: this.question,
        })
    }

    getTitle(locale, defaultLocale, initialLocale) {
        let text = ''
        if (this.question.content.shortTitle.has(locale)) {
            ({ text } = this.question.content.shortTitle.get(locale))
        } else if (this.question.content.shortTitle.has(defaultLocale)) {
            ({ text } = this.question.content.shortTitle.get(defaultLocale))
        } else if (this.question.content.shortTitle.has(initialLocale)) {
            ({ text } = this.question.content.shortTitle.get(initialLocale))
        }
        return unescapeHtmlTags(text)
    }

    toJSON() {
        const json = {
            renderKey: this.renderKey,
            allowEdit: this.allowEdit,
            isHidden: this.isHidden,
            mandatory: this.mandatory,
            question: this.question,
        }

        if (this.id) {
            json.id = this.id
        }

        return json
    }
}

class FailedQuestion extends Question {
    isFailed = true

    constructor() {
        try {
            super({
                metadata: {
                    initialLocale: 'en_GB',
                },
                content: {
                    shortTitle: {
                        en_GB: {
                            text: 'Question failed to load',
                        },
                    },
                    longTitle: {},
                    instruction: {},
                    aboveText: {},
                    belowText: {},
                    content: {},
                },
                id: uuidv4(),
                questionTypeCode: 'FAILED',
            })
        } catch (e) {
            console.error(e)
            throw e
        }
    }

    hasOptions(set1) {
        return false
    }

    hasHeaderGroup() {
        return false
    }

    toJSON() {
        return super.toJSON()
    }
}

class SingleAxisQuestion extends Question {
    /**
     * @param {Object} options
     * @param {optionSet} options.optionSet
     * @param {Object} options.question
     */

    @observable headerGroups

    constructor({
        optionSet,
        headerGroups = [],
        ...question
    }) {
        super(question)
        makeObservable(this)
        this.optionSet = optionSet instanceof OptionSet ? optionSet : new OptionSet(optionSet)
        if (Array.isArray(headerGroups)) {
            this.headerGroups = headerGroups.map((headerGroup) => (
                headerGroup instanceof HeaderGroup
                    ? headerGroup
                    : new HeaderGroup(headerGroup)
            ))
        }
    }

    addHeaderGroup = () => {
        if (R.isNil(this.headerGroups)) {
            this.headerGroups = observable([])
        }

        console.log('addHeaderGroup', this.headerGroups)

        this.headerGroups.push(new HeaderGroup({}))
    }

    hasOptions(set1) {
        return this.optionSet.options.some((option) => option.id === set1)
    }

    addHeaderGroup = () => {
        if (R.isNil(this.headerGroups)) {
            this.headerGroups = observable([new HeaderGroup({})])
        } else {
            const headerGroups = this.headerGroups.slice()
            headerGroups.push(new HeaderGroup({}))
            this.headerGroups = observable(headerGroups)
        }
    }

    removeHeader = (index) => {
        this.headerGroups = observable(
            this.headerGroups.slice().filter((_, i) => i !== index),
        )
    }

    hasHeaderGroup() {
        return this.headerGroups.length > 0
    }

    hasHeaderGroup() {
        return this.headerGroups.length > 0
    }

    toJSON() {
        const json = Object.assign(
            {},
            super.toJSON(),
            {
                optionSet: this.optionSet.toJSON(),
                headerGroups: this.headerGroups.length === 0
                    ? undefined
                    : this.headerGroups.toJSON(),
            },
        )

        return json
    }
}

class DoubleAxisQuestion extends Question {
    /**
     * @param {Object} options
     * @param {QuestionSet} options.set1
     * @param {QuestionSet} options.set2
     * @param {Object} options.question
     */

    constructor({ optionSetX, optionSetY, ...question }) {
        super(question)
        this.optionSetX = optionSetX instanceof OptionSet ? optionSetX : new OptionSet(optionSetX)
        this.optionSetY = optionSetY instanceof OptionSet ? optionSetY : new OptionSet(optionSetY)
    }

    hasOptions(set1, set2) {
        return this.optionSetX.options.some((option) => option.id === set2)
            && this.optionSetY.options.some((option) => option.id === set1)
    }

    toJSON() {
        const json = Object.assign(
            {},
            super.toJSON(),
            {
                optionSetX: this.optionSetX.toJSON(),
                optionSetY: this.optionSetY.toJSON(),
            },
        )

        return json
    }
}

class CountryListQuestionFrontendSettings {
    constructor({
        displayCols = 1,
        layout = 'D',
        alignment = 'V',
        minChosen,
        maxChosen,
        fieldType = 'SB',
        classNames,
        template,
    }) {
        this.displayCols = displayCols
        this.layout = layout
        this.alignment = alignment
        this.minChosen = minChosen
        this.maxChosen = maxChosen
        this.fieldType = fieldType
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            layout: this.layout,
            alignment: this.alignment,
            minChosen: this.minChosen,
            maxChosen: this.maxChosen,
            fieldType: this.fieldType,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class CountryListQuestionReportSettings {
    constructor({ graphType = 'bar', dashboardBarRanges = '{}', dspYourScore = false }) {
        this.graphType = graphType
        this.dspYourScore = dspYourScore
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
    }

    toJSON() {
        return {
            graphType: this.graphType,
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
            dspYourScore: this.dspYourScore,
        }
    }
}

class CountryListQuestionSettings {
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof CountryListQuestionFrontendSettings
            ? frontend : new CountryListQuestionFrontendSettings(frontend)
        this.report = report instanceof CountryListQuestionFrontendSettings
            ? report : new CountryListQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class CountryListQuestion extends SingleAxisQuestion {
    constructor({ settings, ...singleAxisQuestion }) {
        super(singleAxisQuestion)
        this.questionTypeCode = 'CO'
        this.settings = settings instanceof CountryListQuestionSettings
            ? settings : new CountryListQuestionSettings(settings)
    }

    toJSON() {
        const json = Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )

        return json
    }
}

class DropdownListQuestionFrontendSettings {
    constructor({
        displayCols = 1, alignment = 'V', layout = 'D', classNames, template, ...rest
    }) {
        this.displayCols = displayCols
        this.alignment = alignment
        this.layout = layout
        this.classNames = classNames
        this.template = template

        if (rest.info) {
            this.info = rest.info instanceof Map
                ? info
                : new Map(Object.entries(rest.info || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }

        if (Array.isArray(rest.switch)) {
            this.switch = rest.switch.map((s) => (
                s instanceof Map
                    ? s
                    : new Map(Object.entries(s || {})
                        .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
            ))
        }
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            layout: this.layout,
            alignment: this.alignment,
            switch: switchToJson(this.switch),
            info: infoToJson(this.info),
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class DropdownListQuestionReportSettings {
    constructor({
        graphType = 'bar',
        dspYourScore = false,
        dashboardBarRanges = '{}',
        symbol,
        allowQuadrantAnalysis = false,
    }) {
        this.graphType = graphType
        this.dspYourScore = dspYourScore
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
        this.symbol = symbol
        this.allowQuadrantAnalysis = allowQuadrantAnalysis
    }

    toJSON() {
        return {
            graphType: this.graphType,
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
            dspYourScore: this.dspYourScore,
            symbol: this.symbol,
            allowQuadrantAnalysis: this.allowQuadrantAnalysis,
        }
    }
}

class DropdownListQuestionSettings {
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof DropdownListQuestionFrontendSettings
            ? frontend : new DropdownListQuestionFrontendSettings(frontend)
        this.report = report instanceof DropdownListQuestionReportSettings
            ? report : new DropdownListQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class DropdownListQuestion extends SingleAxisQuestion {
    constructor({ settings, ...singleAxisQuestion }) {
        super(singleAxisQuestion)
        this.questionTypeCode = 'SB'
        this.settings = settings instanceof DropdownListQuestionSettings
            ? settings : new DropdownListQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

class GridQuestionFrontendSettings {
    constructor({
        displayCols = 1,
        isFlipped = false,
        layout = 'D',
        showGradient = false,
        renderLines = false,
        classNames,
        template,
    }) {
        this.displayCols = displayCols
        this.isFlipped = isFlipped
        this.layout = layout
        this.showGradient = showGradient
        this.renderLines = renderLines
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            isFlipped: this.isFlipped,
            layout: this.layout,
            showGradient: this.showGradient,
            renderLines: this.renderLines,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class GridQuestionReportSettings {
    constructor({ graphType = 'bar', dashboardRanges = '{}', allowQuadrantAnalysis = false }) {
        this.graphType = graphType
        this.allowQuadrantAnalysis = allowQuadrantAnalysis
        try {
            this.dashboardRanges = JSON.parse(dashboardRanges)
        } catch (e) {
            this.dashboardRanges = {}
        }
    }

    toJSON() {
        return {
            graphType: this.graphType,
            dashboardRanges: JSON.stringify(this.dashboardRanges),
            allowQuadrantAnalysis: this.allowQuadrantAnalysis,
        }
    }
}

class GridQuestionSettings {
    /**
     * @param {Object} options
     * @param {GridQuestionFrontendSettings} options.frontend
     * @param {GridQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof GridQuestionFrontendSettings
            ? frontend : new GridQuestionFrontendSettings(frontend)
        this.report = report instanceof GridQuestionReportSettings
            ? report : new GridQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class GridQuestion extends DoubleAxisQuestion {
    /**
     * @param {Object} options
     * @param {GridQuestionSettings} options.settings
     * @param {DoubleAxisQuestion} options.doubleAxisQuestion
     */

    @observable headerGroups

    constructor({
        settings,
        headerGroups = [],
        ...doubleAxisQuestion
    }) {
        super(doubleAxisQuestion)
        makeObservable(this)

        this.questionTypeCode = 'TB'
        this.settings = settings instanceof GridQuestionSettings
            ? settings : new GridQuestionSettings(settings)
        if (Array.isArray(headerGroups)) {
            this.headerGroups = headerGroups.map((headerGroup) => (
                headerGroup instanceof HeaderGroup
                    ? headerGroup
                    : new HeaderGroup(headerGroup)
            ))
        }
    }

    addHeaderGroup = () => {
        if (R.isNil(this.headerGroups)) {
            this.headerGroups = observable([new HeaderGroup({})])
        } else {
            const headerGroups = this.headerGroups.slice()
            headerGroups.push(new HeaderGroup({}))
            this.headerGroups = observable(headerGroups)
        }
    }

    removeHeader = (index) => {
        this.headerGroups = observable(
            this.headerGroups.slice().filter((_, i) => i !== index),
        )
    }

    hasHeaderGroup() {
        return this.headerGroups.length > 0
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
                headerGroups: this.headerGroups.length === 0
                    ? undefined
                    : this.headerGroups.toJSON(),
            },
        )
    }
}

/* --- SmartGridQuestion --- */
class SmartGridQuestionFrontendSettings {
    constructor({
        displayCols = 1,
        isFlipped = false,
        layout = 'D',
        showGradient = false,
        classNames,
        template,
    }) {
        this.displayCols = displayCols
        this.isFlipped = isFlipped
        this.layout = layout
        this.showGradient = showGradient
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            isFlipped: this.isFlipped,
            layout: this.layout,
            showGradient: this.showGradient,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class SmartGridQuestionReportSettings {
    constructor({
        graphType = 'bar', dashboardRanges = '{}', dspYourScore = false, allowQuadrantAnalysis = false,
    }) {
        this.graphType = graphType
        this.dspYourScore = dspYourScore
        this.allowQuadrantAnalysis = allowQuadrantAnalysis
        try {
            this.dashboardRanges = JSON.parse(dashboardRanges)
        } catch (e) {
            this.dashboardRanges = {}
        }
    }

    toJSON() {
        return {
            graphType: this.graphType,
            dashboardRanges: JSON.stringify(this.dashboardRanges),
            dspYourScore: this.dspYourScore,
            allowQuadrantAnalysis: this.allowQuadrantAnalysis,
        }
    }
}

class SmartGridQuestionSettings {
    /**
     * @param {Object} options
     * @param {SmartGridQuestionFrontendSettings} options.frontend
     * @param {SmartGridQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof SmartGridQuestionFrontendSettings
            ? frontend : new SmartGridQuestionFrontendSettings(frontend)
        this.report = report instanceof SmartGridQuestionReportSettings
            ? report : new SmartGridQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class SmartGridQuestion extends Question {
    /**
     * @param {Object} options
     * @param {SmartGridQuestionSettings} options.settings
     * @param {Question} options.question
     */
    constructor({
        settings,
        optionSetX,
        optionSetY,
        headerGroups = [],
        ...question
    }) {
        super(question)

        this.questionTypeCode = 'SG'
        this.optionSetX = optionSetX instanceof OptionSet ? optionSetX : new OptionSet(optionSetX)
        this.optionSetY = optionSetY instanceof OptionSet ? optionSetY : new OptionSet(optionSetY || {})
        this.settings = settings instanceof SmartGridQuestionSettings
            ? settings : new SmartGridQuestionSettings(settings)
        if (Array.isArray(headerGroups)) {
            this.headerGroups = headerGroups.map((headerGroup) => (
                headerGroup instanceof HeaderGroup
                    ? headerGroup
                    : new HeaderGroup(headerGroup)
            ))
        }
        console.log('SmartGridQuestion')
    }

    hasOptions(set1, set2, pageQuestion) {
        return this.optionSetX.options.some((option) => option.id === set2)
            && pageQuestion.optionSetY.options.some((option) => option.id === set1)
    }

    addHeaderGroup = () => {
        if (R.isNil(this.headerGroups)) {
            this.headerGroups = observable([new HeaderGroup({})])
        } else {
            const headerGroups = this.headerGroups.slice()
            headerGroups.push(new HeaderGroup({}))
            this.headerGroups = observable(headerGroups)
        }
    }

    removeHeader = (index) => {
        this.headerGroups = observable(
            this.headerGroups.slice().filter((_, i) => i !== index),
        )
    }

    hasHeaderGroup() {
        return this.headerGroups.length > 0
    }

    toJSON() {
        console.log(`SG toJSON -> ${JSON.stringify(this.optionSetY, null, 4)}`)
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                optionSetY: this.optionSetY.toJSON(),
                optionSetX: this.optionSetX.toJSON(),
                settings: this.settings.toJSON(),
            },
        )
    }
}

class MultipleChoiceQuestionFrontendSettings {
    constructor({
        displayCols = 1,
        layout = 'D',
        alignment = 'V',
        minChosen,
        maxChosen,
        showGradient = false,
        classNames,
        template,
    }) {
        this.displayCols = displayCols
        this.layout = layout
        this.alignment = alignment
        this.minChosen = minChosen
        this.maxChosen = maxChosen
        this.showGradient = showGradient
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            layout: this.layout,
            alignment: this.alignment,
            minChosen: this.minChosen,
            maxChosen: this.maxChosen,
            showGradient: this.showGradient,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class MultipleChoiceQuestionReportSettings {
    constructor({
        dashboardRanges = '{}',
        graphType = 'bar',
        dspYourScore = false,
        symbol,
    }) {
        try {
            this.dashboardRanges = JSON.parse(dashboardRanges)
        } catch (e) {
            this.dashboardRanges = {}
        }
        this.graphType = graphType
        this.dspYourScore = dspYourScore
        this.symbol = symbol
    }

    toJSON() {
        return {
            dashboardRanges: JSON.stringify(this.dashboardRanges),
            graphType: this.graphType,
            dspYourScore: this.dspYourScore,
            symbol: this.symbol,
        }
    }
}

class MultipleChoiceQuestionSettings {
    /**
     * @param {Object} options
     * @param {MultipleChoiceQuestionFrontendSettings} options.frontend
     * @param {MultipleChoiceQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof MultipleChoiceQuestionFrontendSettings
            ? frontend : new MultipleChoiceQuestionFrontendSettings(frontend)
        this.report = report instanceof MultipleChoiceQuestionReportSettings
            ? report : new MultipleChoiceQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class MultipleChoiceQuestion extends SingleAxisQuestion {
    constructor({ settings, ...singleAxisQuestion }) {
        super(singleAxisQuestion)

        this.questionTypeCode = 'CH'
        this.settings = settings instanceof MultipleChoiceQuestionSettings
            ? settings : new MultipleChoiceQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

class NumericQuestionFrontendSettings {
    constructor({
        step = 1,
        min = 0,
        max,
        layout = 'D',
        prefix,
        postfix,
        classNames,
        nonRequired,
        nonRequiredText,
        template,
        ...rest
    }) {
        this.step = step
        this.min = min
        this.max = max
        this.layout = layout
        this.classNames = classNames
        this.nonRequired = nonRequired
        this.nonRequiredText = nonRequiredText
        this.template = template

        this.prefix = prefix instanceof Map
            ? prefix
            : new Map(Object.entries(prefix || {})
                .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))

        this.postfix = postfix instanceof Map
            ? postfix
            : new Map(Object.entries(postfix || {})
                .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))

        if (rest.info) {
            this.info = rest.info instanceof Map
                ? info
                : new Map(Object.entries(rest.info || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }

        if (Array.isArray(rest.switch)) {
            this.switch = rest.switch.map((s) => (
                s instanceof Map
                    ? s
                    : new Map(Object.entries(s || {})
                        .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
            ))
        }
    }

    toJSON() {
        return {
            step: this.step,
            min: this.min,
            max: this.max,
            layout: this.layout,
            prefix: mapToJSON(this.prefix),
            postfix: mapToJSON(this.postfix),
            switch: switchToJson(this.switch),
            info: infoToJson(this.info),
            nonRequired: this.nonRequired,
            nonRequiredText: this.nonRequiredText,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class NumericQuestionReportSettings {
    constructor({ graphType = 'bar', dashboardBarRanges = '{}', allowQuadrantAnalysis = false, isRegistration = false }) {
        this.graphType = graphType
        this.allowQuadrantAnalysis = allowQuadrantAnalysis
        this.isRegistration = isRegistration
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
    }

    toJSON() {
        return {
            graphType: this.graphType,
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
            allowQuadrantAnalysis: this.allowQuadrantAnalysis,
            isRegistration: this.isRegistration,
        }
    }
}

class NumericQuestionSettings {
    /**
     * @param {Object} options
     * @param {NumericQuestionFrontendSettings} options.frontend
     * @param {NumericQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof NumericQuestionFrontendSettings
            ? frontend : new NumericQuestionFrontendSettings(frontend)
        this.report = report instanceof NumericQuestionReportSettings
            ? report : new NumericQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class NumericQuestion extends Question {
    constructor({ settings, ...question }) {
        super(question)

        this.questionTypeCode = 'I'
        this.settings = settings instanceof NumericQuestionSettings
            ? settings : new NumericQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

class PercentageQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number} options.displayCols
     * @param {String} options.alignment
     * @param {Number=} options.maxRankedItems
     */

    constructor({
        displayCols = 1,
        alignment = 'V',
        layout = 'D',
        format = '%',
        max = 100,
        min = 0,
        inheritMax,
        classNames,
        sliderLabels,
        ignoreTotalValidation,
        hideValueIndicator,
        hideTotalIndicator,
        template,
    }) {
        this.displayCols = displayCols
        this.alignment = alignment
        this.layout = layout
        this.format = format
        this.max = max
        this.min = min
        this.inheritMax = inheritMax
        this.classNames = classNames
        this.sliderLabels = Array.isArray(sliderLabels)
            ? sliderLabels.map((label) => (
                label instanceof Map
                    ? label
                    : new Map(Object.entries(label || {})
                        .map(([langCode, lang]) => ([langCode, lang])))
            ))
            : undefined

        this.ignoreTotalValidation = Boolean(ignoreTotalValidation)
        this.hideValueIndicator = Boolean(hideValueIndicator)
        this.hideTotalIndicator = Boolean(hideTotalIndicator)
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            alignment: this.alignment,
            layout: this.layout,
            format: this.format,
            max: this.max,
            min: this.min,
            inheritMax: this.inheritMax,
            classNames: this.classNames,
            ignoreTotalValidation: this.ignoreTotalValidation,
            hideValueIndicator: this.hideValueIndicator,
            hideTotalIndicator: this.hideTotalIndicator,
            sliderLabels: Array.isArray(this.sliderLabels)
                ? this.sliderLabels.map((label) => mapToJSON(label))
                : undefined,
            template: this.template,
        }
    }
}

class PercentageQuestionReportSettings {
    constructor({
        graphType = 'bar', dashboardBarRanges = '{}', dspYourScore = false, hideZero = false, allowQuadrantAnalysis = false,
    }) {
        this.graphType = graphType
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
        this.dspYourScore = dspYourScore
        this.hideZero = hideZero
        this.allowQuadrantAnalysis = allowQuadrantAnalysis
    }

    toJSON() {
        return {
            graphType: this.graphType,
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
            dspYourScore: this.dspYourScore,
            hideZero: this.hideZero,
            allowQuadrantAnalysis: this.allowQuadrantAnalysis,
        }
    }
}

class PercentageQuestionSettings {
    /**
     * @param {Object} options
     * @param {PercentageQuestionFrontendSettings} options.frontend
     * @param {PercentageQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof PercentageQuestionFrontendSettings
            ? frontend : new PercentageQuestionFrontendSettings(frontend)
        this.report = report instanceof PercentageQuestionReportSettings
            ? report : new PercentageQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class PercentageQuestion extends SingleAxisQuestion {
    constructor({ settings, ...singleAxisQuestion }) {
        super(singleAxisQuestion)

        this.questionTypeCode = 'PC'
        this.settings = settings instanceof PercentageQuestionSettings
            ? settings : new PercentageQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

class PriorityQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number} options.displayCols
     * @param {String} options.alignment
     * @param {String} options.layout
     * @param {Number=} options.maxRankedItems
     */

    constructor({
        displayCols = 1,
        alignment = 'V',
        layout = 'D',
        maxRankedItems,
        classNames,
        template,
    }) {
        this.displayCols = displayCols
        this.alignment = alignment
        this.layout = layout
        this.maxRankedItems = maxRankedItems
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            alignment: this.alignment,
            layout: this.layout,
            maxRankedItems: this.maxRankedItems,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class PriorityQuestionReportSettings {
    constructor({ graphType = 'bar', dashboardBarRanges = '{}' }) {
        this.graphType = graphType
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
    }

    toJSON() {
        return {
            graphType: this.graphType,
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
        }
    }
}

class PriorityQuestionSettings {
    /**
     * @param {Object} options
     * @param {PriorityQuestionFrontendSettings} options.frontend
     * @param {PriorityQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof PriorityQuestionFrontendSettings
            ? frontend : new PriorityQuestionFrontendSettings(frontend)
        this.report = report instanceof PriorityQuestionReportSettings
            ? report : new PriorityQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class PriorityQuestion extends SingleAxisQuestion {
    /**
     * @param {Object} options
     * @param {PriorityQuestionSettings} options.settings
     * @param {SingleAxisQuestion} options.singleAxisQuestion
     */

    constructor({ settings, ...singleAxisQuestion }) {
        super(singleAxisQuestion)

        this.questionTypeCode = 'OD'
        this.settings = settings instanceof PriorityQuestionSettings
            ? settings : new PriorityQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

class RatingQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number} options.displayCols
     * @param {Boolean} options.isFlipped
     * @param {layout} options.layout
     */

    constructor({
        displayCols = 1, isFlipped = false, layout = 'D', classNames, template,
    }) {
        this.displayCols = displayCols
        this.isFlipped = isFlipped
        this.layout = layout
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            isFlipped: this.isFlipped,
            layout: this.layout,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class RatingQuestionReportSettings {
    constructor({ graphType = 'bar', dashboardBarRanges = '{}' }) {
        this.graphType = graphType
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
    }

    toJSON() {
        return {
            graphType: this.graphType,
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
        }
    }
}

class RatingQuestionSettings {
    /**
     * @param {Object} options
     * @param {RatingQuestionFrontendSettings} options.frontend
     * @param {RatingQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof RatingQuestionFrontendSettings
            ? frontend : new RatingQuestionFrontendSettings(frontend)
        this.report = report instanceof RatingQuestionReportSettings
            ? report : new RatingQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class RatingQuestion extends DoubleAxisQuestion {
    constructor({ settings, ...doubleAxisQuestion }) {
        super(doubleAxisQuestion)

        this.questionTypeCode = 'RT'
        this.settings = settings instanceof RatingQuestionSettings
            ? settings : new RatingQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

class SingleChoiceQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number} options.displayCols
     * @param {String} options.alignment
     * @param {layout} options.layout
     * @param {Boolean} options.showGradient
     * @param {Boolean} options.renderNPS
     */

    constructor({
        displayCols = 1,
        alignment = 'V',
        layout = 'D',
        showGradient = false,
        renderNPS = false,
        classNames,
        template,
    }) {
        this.displayCols = displayCols
        this.alignment = alignment
        this.layout = layout
        this.showGradient = showGradient
        this.renderNPS = renderNPS
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            alignment: this.alignment,
            layout: this.layout,
            showGradient: this.showGradient,
            renderNPS: this.renderNPS,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class SingleChoiceQuestionReportSettings {
    constructor({
        dashboardBarRanges = '{}',
        graphType = 'bar',
        dspYourScore = false,
        symbol,
        allowQuadrantAnalysis = false,
        isRegistration = true,
    }) {
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
        this.graphType = graphType
        this.dspYourScore = dspYourScore
        this.symbol = symbol
        this.allowQuadrantAnalysis = allowQuadrantAnalysis
        this.isRegistration = isRegistration
    }

    toJSON() {
        return {
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
            graphType: this.graphType,
            dspYourScore: this.dspYourScore,
            symbol: this.symbol,
            allowQuadrantAnalysis: this.allowQuadrantAnalysis,
            isRegistration: this.isRegistration,
        }
    }
}

class SingleChoiceQuestionSettings {
    /**
     * @param {Object} options
     * @param {SingleChoiceQuestionFrontendSettings} options.frontend
     * @param {SingleChoiceQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof SingleChoiceQuestionFrontendSettings
            ? frontend : new SingleChoiceQuestionFrontendSettings(frontend)
        this.report = report instanceof SingleChoiceQuestionReportSettings
            ? report : new SingleChoiceQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class SingleChoiceQuestion extends SingleAxisQuestion {
    constructor({ settings, ...singleAxisQuestion }) {
        super(singleAxisQuestion)

        this.questionTypeCode = 'RD'
        this.settings = settings instanceof SingleChoiceQuestionSettings
            ? settings : new SingleChoiceQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

class StaticText extends Question {
    constructor({ ...question }) {
        super(question)
        this.questionTypeCode = 'ST'
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
            },
        )
    }
}

class TextAreaQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number=} options.wordLimit
     * @param {Number=} options.charLimit
     * @param {Number} options.fieldWidth
     */

    constructor({
        wordLimit, charLimit = 2000, fieldWidth = 110, classNames, template,
    }) {
        this.wordLimit = wordLimit
        this.charLimit = charLimit
        this.fieldWidth = fieldWidth
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            wordLimit: this.wordLimit,
            charLimit: this.charLimit,
            fieldWidth: this.fieldWidth,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class TextAreaQuestionReportSettings {
    constructor({ graphType = 'bar', allowImports }) {
        this.graphType = graphType
        this.allowImports = allowImports
    }

    toJSON() {
        return {
            graphType: this.graphType,
            allowImports: this.allowImports,
        }
    }
}

class TextAreaQuestionSettings {
    /**
     * @param {Object} options
     * @param {TextAreaQuestionFrontendSettings} options.frontend
     * @param {TextAreaQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof TextAreaQuestionFrontendSettings
            ? frontend : new TextAreaQuestionFrontendSettings(frontend)
        this.report = report instanceof TextAreaQuestionReportSettings
            ? report : new TextAreaQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class TextAreaQuestion extends Question {
    constructor({ settings, ...question }) {
        super(question)
        this.questionTypeCode = 'TA'
        this.settings = settings instanceof TextAreaQuestionSettings
            ? settings : new TextAreaQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

const switchToJson = (v) => {
    console.log('switchToJson', JSON.stringify(switchToJson, null, 4))
    const a = Array.isArray(v)
        ? v.map(mapToJSON)
        : undefined
    return a
}

const infoToJson = (v) => {
    console.log('infoToJson', JSON.stringify(infoToJson, null, 4))
    const a = v instanceof Map
        ? mapToJSON(v)
        : undefined
    return a
}

class TextBoxQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number=} options.wordLimit
     * @param {Number=} options.charLimit
     * @param {Number} options.fieldWidth
     */

    constructor({
        wordLimit, charLimit = 500, prefix, postfix, validation, classNames, template, ...rest
    }) {
        this.wordLimit = wordLimit
        this.charLimit = charLimit
        this.classNames = classNames
        this.validation = validation
        this.template = template

        this.prefix = prefix instanceof Map
            ? prefix
            : new Map(Object.entries(prefix || {})
                .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        this.postfix = postfix instanceof Map
            ? postfix
            : new Map(Object.entries(postfix || {})
                .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))

        if (rest.info) {
            this.info = rest.info instanceof Map
                ? info
                : new Map(Object.entries(rest.info || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }

        if (Array.isArray(rest.switch)) {
            this.switch = rest.switch.map((s) => (
                s instanceof Map
                    ? s
                    : new Map(Object.entries(s || {})
                        .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
            ))
        }
    }

    toJSON() {
        return {
            wordLimit: this.wordLimit,
            charLimit: this.charLimit,
            prefix: mapToJSON(this.prefix),
            postfix: mapToJSON(this.postfix),
            switch: switchToJson(this.switch),
            info: infoToJson(this.info),
            validation: this.validation,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class TextBoxQuestionReportSettings {
    constructor({ graphType = 'bar', allowImports }) {
        this.graphType = graphType
        this.allowImports = allowImports
    }

    toJSON() {
        return {
            graphType: this.graphType,
            allowImports: this.allowImports,
        }
    }
}

class TextBoxQuestionSettings {
    /**
     * @param {Object} options
     * @param {TextBoxQuestionFrontendSettings} options.frontend
     * @param {TextBoxQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof TextBoxQuestionFrontendSettings
            ? frontend : new TextBoxQuestionFrontendSettings(frontend)
        this.report = report instanceof TextBoxQuestionReportSettings
            ? report : new TextBoxQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class TextBoxQuestion extends Question {
    constructor({ settings, ...question }) {
        super(question)
        this.questionTypeCode = 'TX'
        this.settings = settings instanceof TextBoxQuestionSettings
            ? settings : new TextBoxQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}

/** UrlVariableQuestion */
class UrlVariableQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number} options.displayCols
     * @param {String} options.alignment
     * @param {layout} options.layout
     * @param {Boolean} options.showGradient
     */

    constructor({
        displayCols = 1,
        alignment = 'V',
        layout = 'D',
        showGradient = false,
        classNames,
        template,
    }) {
        this.displayCols = displayCols
        this.alignment = alignment
        this.layout = layout
        this.showGradient = showGradient
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            alignment: this.alignment,
            layout: this.layout,
            showGradient: this.showGradient,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class UrlVariableQuestionReportSettings {
    constructor({
        dashboardBarRanges = '{}',
        graphType = 'bar',
        dspYourScore = false,
        symbol,
    }) {
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
        this.graphType = graphType
        this.dspYourScore = dspYourScore
        this.symbol = symbol
    }

    toJSON() {
        return {
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
            graphType: this.graphType,
            dspYourScore: this.dspYourScore,
            symbol: this.symbol,
        }
    }
}

class UrlVariableQuestionSettings {
    /**
     * @param {Object} options
     * @param {SingleChoiceQuestionFrontendSettings} options.frontend
     * @param {UrlVariableQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof UrlVariableQuestionFrontendSettings
            ? frontend : new UrlVariableQuestionFrontendSettings(frontend)
        this.report = report instanceof UrlVariableQuestionReportSettings
            ? report : new UrlVariableQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class UrlVariableQuestion extends SingleAxisQuestion {
    constructor({ settings, ...singleAxisQuestion }) {
        super(singleAxisQuestion)

        this.questionTypeCode = 'UV'
        this.settings = settings instanceof UrlVariableQuestionSettings
            ? settings : new UrlVariableQuestionSettings(settings)
    }

    toJSON() {
        return {

            ...super.toJSON(),
            questionTypeCode: this.questionTypeCode,
            settings: this.settings.toJSON(),
        }
    }
}
/** end UV */

/** TextBoxListQuestion */
class TextBoxListQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number} options.displayCols
     * @param {String} options.alignment
     * @param {layout} options.layout
     * @param {Boolean} options.showGradient
     */

    constructor({
        displayCols = 1,
        alignment = 'V',
        layout = 'D',
        showGradient = false,
        classNames,
        template,
    }) {
        this.displayCols = displayCols
        this.alignment = alignment
        this.layout = layout
        this.showGradient = showGradient
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            displayCols: this.displayCols,
            alignment: this.alignment,
            layout: this.layout,
            showGradient: this.showGradient,
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class TextBoxListQuestionReportSettings {
    constructor({
        dashboardBarRanges = '{}',
        graphType = 'bar',
        dspYourScore = false,
        symbol,
    }) {
        try {
            this.dashboardBarRanges = JSON.parse(dashboardBarRanges)
        } catch (e) {
            this.dashboardBarRanges = {}
        }
        this.graphType = graphType
        this.dspYourScore = dspYourScore
        this.symbol = symbol
    }

    toJSON() {
        return {
            dashboardBarRanges: JSON.stringify(this.dashboardBarRanges),
            graphType: this.graphType,
            dspYourScore: this.dspYourScore,
            symbol: this.symbol,
        }
    }
}

class TextBoxListQuestionSettings {
    /**
     * @param {Object} options
     * @param {SingleChoiceQuestionFrontendSettings} options.frontend
     * @param {UrlVariableQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof TextBoxListQuestionFrontendSettings
            ? frontend : new TextBoxListQuestionFrontendSettings(frontend)
        this.report = report instanceof TextBoxListQuestionReportSettings
            ? report : new TextBoxListQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class TextBoxListQuestion extends SingleAxisQuestion {
    constructor({ settings, ...singleAxisQuestion }) {
        super(singleAxisQuestion)

        this.questionTypeCode = 'TL'
        this.settings = settings instanceof TextBoxListQuestionSettings
            ? settings : new TextBoxListQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
            },
        )
    }
}
/** end TL */

/** FM  */
class FormQuestion extends Question {
    constructor({ optionSet = [], ...formQuestion }) {
        super(formQuestion)

        this.questionTypeCode = 'FM'
        this.optionSet = optionSet
            .map((option) => (option instanceof FormOptionSet ? option : new FormOptionSet(option)))
    }

    addOption(option) {
        const questionOption = new FormOptionSet(option)
        if (option.allowEdit) {
            questionOption.allowEdit = true
        }
        this.optionSet.push(questionOption)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                optionSet: null,
            },
        )
    }
}
/** end FM */

/** NF  */
/** DateQuestionFrontendSettings */
class NumberFormQuestionSettings {
    /**
     * @param {Object} options
     * @param {Boolean} options.isCurrency
     * @param {String} options.classNames
     */
    constructor({
        isCurrency = false, classNames, nonRequired, nonRequiredText, template, allowQuadrantAnalysis = false, ...rest
    }) {
        this.isCurrency = isCurrency
        this.classNames = classNames
        this.nonRequired = rest.switch ? nonRequired : undefined
        this.nonRequiredText = rest.switch ? nonRequiredText : undefined
        this.template = template
        this.allowQuadrantAnalysis = allowQuadrantAnalysis

        if (Array.isArray(rest.switch)) {
            this.switch = rest.switch.map((s) => (
                s instanceof Map
                    ? s
                    : new Map(Object.entries(s || {})
                        .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
            ))
        }
    }

    toJSON() {
        return {
            isCurrency: this.isCurrency,
            classNames: this.classNames,
            nonRequired: this.nonRequired,
            nonRequiredText: this.nonRequiredText,
            switch: switchToJson(this.switch),
            template: this.template,
            allowQuadrantAnalysis: this.allowQuadrantAnalysis,
        }
    }
}

class NumberFormQuestion extends Question {
    constructor({ optionSet = [], settings = {}, ...formQuestion }) {
        super(formQuestion)

        this.questionTypeCode = 'NF'
        this.optionSet = optionSet
        .map((option) => (option instanceof FormOptionSet ? option : new FormOptionSet(option)))
        this.settings = settings instanceof NumberFormQuestionSettings
            ? settings
            : new NumberFormQuestionSettings(settings)
    }

    addOption(option) {
        const questionOption = new FormOptionSet(option)
        if (option.allowEdit) {
            questionOption.allowEdit = true
        }
        this.optionSet.push(questionOption)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings.toJSON(),
                optionSet: null,
            },
        )
    }
}
/** end NF */

/** DT  */
/** DateQuestionFrontendSettings */
class DateQuestionFrontendSettings {
    /**
     * @param {Object} options
     * @param {Number} options.displayCols
     * @param {String} options.alignment
     * @param {layout} options.layout
     * @param {Boolean} options.showGradient
     */

    constructor({
        placeHolder = ['', ''],
        renderType = 'D',
        classNames,
        template,
        ...rest
    }) {
        this.placeHolder = placeHolder.map((s) => (
            s instanceof Map
                ? s
                : new Map(Object.entries(s || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        ))
        this.renderType = renderType

        if (rest.info) {
            this.info = rest.info instanceof Map
                ? info
                : new Map(Object.entries(rest.info || {})
                    .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
        }

        if (Array.isArray(rest.switch)) {
            this.switch = rest.switch.map((s) => (
                s instanceof Map
                    ? s
                    : new Map(Object.entries(s || {})
                        .map(([langCode, lang]) => ([langCode, new QuestionLang(lang)])))
            ))
        }
        this.classNames = classNames
        this.template = template
    }

    toJSON() {
        return {
            placeHolder: switchToJson(this.placeHolder),
            renderType: this.renderType,
            switch: switchToJson(this.switch),
            info: infoToJson(this.info),
            classNames: this.classNames,
            template: this.template,
        }
    }
}

class DateQuestionReportSettings {
    constructor({
        graphType = 'bar',
    }) {
        this.graphType = graphType
    }

    toJSON() {
        return {
            graphType: this.graphType,
        }
    }
}

class DateQuestionSettings {
    /**
     * @param {Object} options
     * @param {SingleChoiceQuestionFrontendSettings} options.frontend
     * @param {UrlVariableQuestionReportSettings} options.report
     */
    constructor({ frontend, report }) {
        this.frontend = frontend instanceof DateQuestionFrontendSettings
            ? frontend : new DateQuestionFrontendSettings(frontend)
        this.report = report instanceof DateQuestionReportSettings
            ? report : new DateQuestionReportSettings(report)
    }

    toJSON() {
        return {
            frontend: this.frontend.toJSON(),
            report: this.report.toJSON(),
        }
    }
}

class DateQuestion extends Question {
    constructor({
        settings, minDate, maxDate, ...formQuestion
    }) {
        super(formQuestion)

        this.questionTypeCode = 'DT'
        this.minDate = minDate
        this.maxDate = maxDate
        this.settings = settings instanceof DateQuestionSettings
            ? settings
            : new DateQuestionSettings(settings)
    }

    toJSON() {
        return Object.assign(
            {},
            super.toJSON(),
            {
                questionTypeCode: this.questionTypeCode,
                settings: this.settings,
                minDate: this.minDate ? this.minDate : undefined,
                maxDate: this.maxDate ? this.maxDate : undefined,
            },
        )
    }
}
/** end DT */

export {
    CountryListQuestion,
    DropdownListQuestion,
    GridQuestion,
    SmartGridQuestion,
    MultipleChoiceQuestion,
    NumericQuestion,
    PercentageQuestion,
    PriorityQuestion,
    RatingQuestion,
    SingleChoiceQuestion,
    StaticText,
    TextAreaQuestion,
    TextBoxQuestion,
    QuestionLang,
    QuestionOptionLang,
    DoubleAxisQuestion,
    Question,
    QuestionOption,
    OptionSet,
    UrlVariableQuestion,
    TextBoxListQuestion,
    FormQuestion,
    DateQuestion,
    NumberFormQuestion,
    FailedQuestion,
}
